/**
 * 워크스페이스 관리 (2026-08-19) — 좌: 조직 트리(용량) / 우: 그 범위의 사람.
 *
 * 시안 SSOT: docs/workspace-quota-sample.html
 * 디자인 SSOT: 같은 콘솔의 사용자 관리 화면(`UserWorkspace.tsx` + `UserWorkspace.css`, `um-*`).
 *
 * "개인별 / 팀별" 세그먼트를 쓰지 않는다. 사용자 관리와 같이 **왼쪽 트리로 범위를 고르고
 * 오른쪽에는 사람만** 놓는다. 팀은 목록의 한 줄이 아니라 **트리 노드**이고, 노드 오른쪽에는
 * 인원수 대신 **용량**이 붙어 조직도 자체가 용량 분포를 보여준다.
 *
 * ⚠ 팀 용량과 팀원 개인 용량은 **다른 것**이다.
 *   - 팀 노드의 값 = 그 팀이 소유한 파일(ownerScope='team')의 용량 — /usage?scope=team
 *   - 우측 목록의 값 = 그 팀에 속한 사람들의 **개인** 용량 — /usage?scope=personal
 *   둘을 더하거나 같은 것으로 보면 안 된다.
 *
 * ⚠ 부서에는 한도가 없다. workspace_quotas 의 scopeType 은 personal|team|company 뿐이라
 *   부서 카드는 **하위 팀의 합계(읽기 전용)** 이고 한도 수정 버튼을 주지 않는다.
 *
 * 서버 SSOT — 전부 이미 있는 것들이다(신규 엔드포인트 0건)
 *  - 조직  : /admin/org/{divisions,teams,teams/:id/members}   (api/org.ts — 사용자 관리가 쓰는 것)
 *  - 용량  : /admin/workspace/{overview,usage,defaults,quota}  (api/workspaceAdmin.ts)
 *
 * 이전 화면 `WorkspaceAdminSection.tsx` 는 롤백 대비로 무수정 보존돼 있다.
 * ⚠ 루트에 `um-ws` 를 반드시 함께 붙인다 — `--um-mono`·`--um-tint-*` 가 그 스코프에만 있다.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import '../AttendancePage/UserManage.css';
import './console-shared.css';
import './WorkspaceQuota.css';
import {
  HardDrive, Users, ChevronRight, MoreVertical, X, AlertTriangle, Check, Edit2,
} from '../../icons';
// 채택 3단계 — 검색칸·고르개·숫자칸·체크·쪽넘김·단추를 정본 공통 컴포넌트로.
import { Button, Checkbox, NumberInput, Pagination, SearchInput, Select } from '../../components/shared';
import {
  getWorkspaceOverview, getWorkspaceUsage, setWorkspaceQuota, getQuotaDefaults,
  formatBytes, bytesToGb, gbToBytes,
  type WorkspaceOverview, type UsageRow, type QuotaScopeType, type QuotaDefaults,
} from '../../api/admin-workspace';
import { listDivisions, listTeams, listTeamMembers, type OrgDivision, type OrgTeam } from '../../api/admin-org';
import { getInstitution } from '../../api/institution';

/* ─────────────────────────── 상수 · 유틸 ─────────────────────────── */

/** 서버 FILE_KINDS 9종. 없는 키는 키를 그대로 보여 준다. */
const KIND_LABEL: Record<string, string> = {
  word: 'Word', excel: 'Excel', ppt: 'PPT', hwp: '한글',
  pdf: 'PDF', markup: '마크업', txt: 'txt', image: '이미지', etc: '그외',
};

/** 용량 상태 — 사용자 계정 상태와 뜻이 다르므로 이름을 따로 둔다. */
type Tone = 'ok' | 'warn' | 'over' | 'unlimited';
const TONE_LABEL: Record<Tone, string> = { ok: '여유', warn: '경고', over: '초과', unlimited: '무제한' };

function toneOf(r: { pct: number; warn: boolean; unlimited: boolean }): Tone {
  if (r.unlimited) return 'unlimited';
  if (r.pct >= 100) return 'over';
  return r.warn ? 'warn' : 'ok';
}

/** 한도가 아예 없는 집계(부서·회사 직속)용 — 상태를 매길 수 없으므로 중립. */
function toneOfPct(used: number, limit: number | null): Tone {
  if (limit == null || limit < 0) return 'unlimited';
  const p = limit === 0 ? 0 : Math.round((used / limit) * 100);
  if (p >= 100) return 'over';
  return p >= 80 ? 'warn' : 'ok';
}

const TONE_OPTS: Array<{ value: string; label: string }> = [
  { value: '', label: '상태 전체' },
  { value: 'over', label: '초과' },
  { value: 'warn', label: '경고' },
  { value: 'ok', label: '여유' },
  { value: 'unlimited', label: '무제한' },
  { value: 'default', label: '기본값 적용' },
];

const SORT_OPTS: Array<{ value: string; label: string }> = [
  { value: 'used:desc', label: '사용량 많은 순' },
  { value: 'pct:desc', label: '한도 대비 높은 순' },
  { value: 'files:desc', label: '파일 많은 순' },
  { value: 'name:asc', label: '이름 가나다' },
];

/** 부서 식별색 — tokens.css 의 accent 팔레트(사용자 관리와 같은 규칙). */
const HUES = [
  'var(--accent-blue)', 'var(--accent-purple)', 'var(--accent-orange)',
  'var(--accent-pink)', 'var(--accent-green)', 'var(--accent-red)',
];
function hueOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length]!;
}
function hueStyle(hue: string): CSSProperties {
  return { '--um-hue': hue } as CSSProperties;
}

const PAGE_SIZE = 20;

const ERR_MAP: Record<string, string> = {
  USER_NOT_FOUND: '사용자를 찾을 수 없습니다.',
  TEAM_NOT_FOUND: '팀을 찾을 수 없습니다.',
  CONFLICT: '다른 작업과 충돌했습니다. 다시 시도하세요.',
};
function errMsg(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { code?: string; message?: string; error?: string; violations?: string[] } }; message?: string };
  const d = r?.response?.data;
  const mapped = d?.code ? ERR_MAP[d.code] : undefined;
  const v = Array.isArray(d?.violations) && d.violations.length ? ` (${d.violations.join(', ')})` : '';
  return `${mapped ?? d?.message ?? d?.error ?? r?.message ?? fallback}${v}`;
}

function initial(name: string, sub: string) {
  return (name || sub || '?').trim().charAt(0).toUpperCase();
}

/**
 * 「안 쓰는 계정」 표시 — **개인·팀 둘 다 `isActive` 하나**로 본다 (2026-08-31).
 *
 * ★★★ 종전에는 개인 행만 `r.status` 를 함께 봤는데, 그 칸은 **접속 상태**(`offline` 등)였다.
 *    그래서 **접속 안 한 사람이 전부 비활성으로 보였다** — 운영에서 11행이 모두 흐려지고
 *    배지에 영문 `offline` 이 그대로 떴다. 계정 상태와 접속 상태는 다른 축이다.
 * ★ 옛 서버가 개인 행에 `isActive` 를 안 주면 **표시가 안 붙는다** — 잘못 붙는 것보다 낫다.
 */
function inactiveLabel(r: UsageRow): string | null {
  return r.isActive === false ? '비활성' : null;
}

/** 부서·팀 정렬 — 서버와 같은 규칙(sortOrder asc)에 이름을 2차 키로. */
function bySortOrder<T extends { sortOrder: number; name: string }>(a: T, b: T): number {
  return (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name, 'ko');
}

/** 동시 요청 상한을 둔 map — 팀 수만큼 멤버 조회가 나가므로 서버를 몰아치지 않는다. */
async function mapPool<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let cursor = 0;
  async function worker() {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      out[i] = await fn(items[i]!);
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return out;
}

type Sel =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'div'; id: string }
  | { kind: 'team'; id: string };

/** 드로어가 여는 대상. 부서·회사 직속은 한도가 없어 대상이 되지 않는다. */
type Target = { scopeType: QuotaScopeType; scopeId?: string; row: UsageRow; kindLabel: string; defaultLimit: number | null };

/* ═══════════════════════════ 본체 ═══════════════════════════ */

export function WorkspaceQuotaWorkspace() {
  const [divs, setDivs] = useState<OrgDivision[]>([]);
  const [teams, setTeams] = useState<OrgTeam[]>([]);
  const [people, setPeople] = useState<UsageRow[]>([]);
  const [teamRows, setTeamRows] = useState<UsageRow[]>([]);
  /** userId → 소속 teamId 목록. 소속의 진실은 팀 멤버십 하나뿐이다. */
  const [memberOf, setMemberOf] = useState<Record<string, string[]>>({});
  const [overview, setOverview] = useState<WorkspaceOverview | null>(null);
  const [defaults, setDefaults] = useState<QuotaDefaults | null>(null);
  const [company, setCompany] = useState('');

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  const [sel, setSel] = useState<Sel>({ kind: 'all' });
  const [openDivs, setOpenDivs] = useState<Record<string, boolean>>({});
  const [orgQ, setOrgQ] = useState('');

  const [q, setQ] = useState('');
  const [tone, setTone] = useState('');
  const [sort, setSort] = useState('used:desc');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<Record<string, true>>({});
  const [bulkGb, setBulkGb] = useState(5);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [target, setTarget] = useState<Target | null>(null);

  /* ── 로딩 ── */
  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // 회사명·기본값은 실패해도 화면을 막지 않는다 — 라벨만 폴백된다.
      const [d, t, inst, ov, dft, pu, tu] = await Promise.all([
        listDivisions(),
        listTeams(),
        getInstitution().catch(() => null),
        getWorkspaceOverview(),
        getQuotaDefaults().catch(() => null),
        getWorkspaceUsage('personal'),
        getWorkspaceUsage('team'),
      ]);
      const lists = await mapPool(t, 4, (team) => listTeamMembers(team.id).catch(() => []));
      const map: Record<string, string[]> = {};
      t.forEach((team, i) => {
        (lists[i] ?? []).forEach((m) => { (map[m.user.id] ??= []).push(team.id); });
      });
      setDivs(d); setTeams(t); setCompany(inst?.name ?? '');
      setOverview(ov); setDefaults(dft);
      setPeople(pu.rows); setTeamRows(tu.rows); setMemberOf(map);
      setOpenDivs((prev) => {
        const next = { ...prev };
        d.forEach((x) => { if (next[x.id] === undefined) next[x.id] = true; });
        return next;
      });
    } catch (e) {
      setErr(errMsg(e, '조직·용량 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { setPage(1); setPicked({}); }, [sel, q, tone, sort]);
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(t);
  }, [notice]);

  /* ── 조직 파생 ── */
  const sortedDivs = useMemo(() => [...divs].sort(bySortOrder), [divs]);
  const teamsOfDiv = useCallback(
    (divId: string | null) => teams.filter((t) => t.divisionId === divId).sort(bySortOrder),
    [teams],
  );
  const rootTeams = useMemo(() => teamsOfDiv(null), [teamsOfDiv]);
  const teamRowById = useMemo(() => {
    const m = new Map<string, UsageRow>();
    teamRows.forEach((r) => m.set(r.scopeId, r));
    return m;
  }, [teamRows]);
  const divById = useMemo(() => new Map(divs.map((d) => [d.id, d])), [divs]);
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  /** 팀이 소유한 파일의 용량(팀원 개인 용량과 다른 것). */
  const teamUsed = useCallback((id: string) => teamRowById.get(id)?.usedBytes ?? 0, [teamRowById]);
  const divUsed = useCallback(
    (divId: string) => teamsOfDiv(divId).reduce((s, t) => s + teamUsed(t.id), 0),
    [teamsOfDiv, teamUsed],
  );
  /** 부서에는 한도 행이 없다 — 하위 팀 한도의 합. 하나라도 무제한이면 무제한. */
  const divLimit = useCallback((divId: string): number => {
    const ts = teamsOfDiv(divId);
    if (ts.length === 0) return 0;
    if (ts.some((t) => (teamRowById.get(t.id)?.unlimited ?? false))) return -1;
    return ts.reduce((s, t) => s + (teamRowById.get(t.id)?.limitBytes ?? 0), 0);
  }, [teamsOfDiv, teamRowById]);

  const teamHue = useCallback(
    (t: OrgTeam) => (t.divisionId ? hueOf(t.divisionId) : hueOf(t.id)),
    [],
  );

  /* ── 사람 범위 ── */
  const unassigned = useMemo(
    () => people.filter((p) => (memberOf[p.scopeId] ?? []).length === 0),
    [people, memberOf],
  );
  const scopedPeople = useMemo(() => {
    if (sel.kind === 'all') return people;
    if (sel.kind === 'none') return unassigned;
    if (sel.kind === 'team') return people.filter((p) => (memberOf[p.scopeId] ?? []).includes(sel.id));
    const ids = new Set(teamsOfDiv(sel.id).map((t) => t.id));
    return people.filter((p) => (memberOf[p.scopeId] ?? []).some((tid) => ids.has(tid)));
  }, [sel, people, unassigned, memberOf, teamsOfDiv]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let arr = scopedPeople;
    if (s) arr = arr.filter((r) => `${r.name} ${r.subtitle ?? ''}`.toLowerCase().includes(s));
    if (tone === 'default') arr = arr.filter((r) => r.isDefault && !r.unlimited);
    else if (tone) arr = arr.filter((r) => toneOf(r) === tone);

    const [key, dir] = sort.split(':');
    const sign = dir === 'desc' ? -1 : 1;
    return [...arr].sort((a, b) => {
      let cmp = 0;
      if (key === 'used') cmp = a.usedBytes - b.usedBytes;
      else if (key === 'pct') cmp = (a.unlimited ? -1 : a.pct) - (b.unlimited ? -1 : b.pct);
      else if (key === 'files') cmp = a.fileCount - b.fileCount;
      else cmp = a.name.localeCompare(b.name, 'ko', { numeric: true });
      if (cmp === 0) cmp = a.name.localeCompare(b.name, 'ko', { numeric: true });
      return cmp * sign;
    });
  }, [scopedPeople, q, tone, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const paged = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const pickedIds = Object.keys(picked);
  const scopeUsed = useMemo(() => filtered.reduce((s, r) => s + r.usedBytes, 0), [filtered]);

  /* ── 저장 반영 ── */
  const applySaved = useCallback((
    which: 'personal' | 'team',
    scopeId: string,
    saved: { limitBytes: number; pct: number; warn: boolean; isDefault: boolean },
  ) => {
    const patch = (r: UsageRow): UsageRow => (
      r.scopeId === scopeId
        ? { ...r, limitBytes: saved.limitBytes, pct: saved.pct, warn: saved.warn, isDefault: saved.isDefault, unlimited: saved.limitBytes < 0 }
        : r
    );
    if (which === 'personal') setPeople((cur) => cur.map(patch));
    else setTeamRows((cur) => cur.map(patch));
  }, []);

  async function applyBulk(limitBytes: number) {
    const ids = [...pickedIds];
    if (ids.length === 0) return;
    setBulkBusy(true); setErr(null); setNotice(null);
    const results = await Promise.allSettled(
      ids.map((scopeId) => setWorkspaceQuota({ scopeType: 'personal', scopeId, limitBytes })),
    );
    let ok = 0; let fail = 0;
    results.forEach((res, i) => {
      const scopeId = ids[i]!;
      if (res.status === 'fulfilled') { ok += 1; applySaved('personal', scopeId, res.value); }
      else fail += 1;
    });
    setNotice({
      text: `일괄 적용 완료 — 성공 ${ok}명${fail ? ` · 실패 ${fail}명` : ''} (→ ${limitBytes < 0 ? '무제한' : formatBytes(limitBytes)})`,
      tone: fail ? 'warn' : 'ok',
    });
    setPicked({});
    setBulkBusy(false);
  }

  /* ── 선택 범위 요약 ── */
  interface ScopeInfo {
    name: string; kindLabel: string; hue: string;
    used: number; limit: number | null; fileCount: number; people: number; sub: string;
    /** 한도를 고칠 수 있는 범위인가 — 부서·회사 직속은 서버에 한도 개념이 없다. */
    editable: null | { scopeType: QuotaScopeType; scopeId?: string; row: UsageRow; defaultLimit: number | null };
  }

  const scope = useMemo<ScopeInfo>(() => {
    if (sel.kind === 'all') {
      const used = overview?.totalBytes ?? 0;
      const limit = overview?.company.unlimited ? -1 : (overview?.company.limitBytes ?? -1);
      const row: UsageRow = {
        scopeId: 'company', name: company || '회사 전체', subtitle: '모든 사용자·팀 합계',
        usedBytes: used, limitBytes: limit, fileCount: overview?.fileCount ?? 0,
        pct: overview?.company.pct ?? 0, unlimited: overview?.company.unlimited ?? true,
        warn: !(overview?.company.unlimited ?? true) && (overview?.company.pct ?? 0) >= 80,
        isDefault: false,
      };
      return {
        name: company || '회사 전체', kindLabel: '회사', hue: 'var(--primary)',
        used, limit, fileCount: overview?.fileCount ?? 0, people: people.length,
        sub: `${divs.length}부서 · ${teams.length}팀`,
        editable: { scopeType: 'company', row, defaultLimit: defaults?.company ?? null },
      };
    }
    if (sel.kind === 'none') {
      return {
        name: '회사 직속', kindLabel: '범위', hue: 'var(--text-subtle)',
        used: unassigned.reduce((s, r) => s + r.usedBytes, 0), limit: null,
        fileCount: unassigned.reduce((s, r) => s + r.fileCount, 0), people: unassigned.length,
        sub: '어느 팀에도 없는 인원', editable: null,
      };
    }
    if (sel.kind === 'div') {
      const d = divById.get(sel.id);
      const ts = teamsOfDiv(sel.id);
      return {
        name: d?.name ?? '부서', kindLabel: '부서', hue: hueOf(sel.id),
        used: divUsed(sel.id), limit: divLimit(sel.id),
        fileCount: ts.reduce((s, t) => s + (teamRowById.get(t.id)?.fileCount ?? 0), 0),
        people: scopedPeople.length, sub: `${ts.length}개 팀 합계`, editable: null,
      };
    }
    const t = teamById.get(sel.id);
    const row = teamRowById.get(sel.id);
    return {
      name: t?.name ?? '팀', kindLabel: '팀', hue: t ? teamHue(t) : 'var(--primary)',
      used: row?.usedBytes ?? 0, limit: row ? (row.unlimited ? -1 : row.limitBytes) : null,
      fileCount: row?.fileCount ?? 0, people: scopedPeople.length,
      sub: t?.divisionName ?? '부서 없음',
      editable: row ? { scopeType: 'team', scopeId: sel.id, row, defaultLimit: defaults?.team ?? null } : null,
    };
  }, [sel, overview, company, people.length, divs.length, teams.length, defaults, unassigned,
      divById, teamsOfDiv, divUsed, divLimit, teamRowById, teamById, teamHue, scopedPeople.length]);

  /* ── 트리 노드 ── */
  const matchQ = useCallback((s: string) => {
    const v = orgQ.trim().toLowerCase();
    return v === '' || s.toLowerCase().includes(v);
  }, [orgQ]);

  function nodeValue(used: number, limit: number | null) {
    const t = toneOfPct(used, limit);
    return (
      <span className={`wsq-nv${t === 'warn' ? ' is-warn' : t === 'over' ? ' is-over' : ''}`}>
        <i aria-hidden />
        <b>{formatBytes(used)}</b>
      </span>
    );
  }

  function renderTeamNode(t: OrgTeam) {
    const row = teamRowById.get(t.id);
    const isSel = sel.kind === 'team' && sel.id === t.id;
    return (
      <div key={t.id} role="treeitem" tabIndex={0} aria-selected={isSel}
        className={`um-node${isSel ? ' is-sel' : ''}${t.isActive ? '' : ' is-inactive'}`}
        style={hueStyle(teamHue(t))}
        onClick={() => setSel({ kind: 'team', id: t.id })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'team', id: t.id }); } }}>
        <span className="um-tw is-leaf" />
        <span className="um-mk um-mk--team"><i /></span>
        <span className="um-node__nm">{t.name}{t.divisionId ? '' : ' (부서 없음)'}</span>
        {!t.isActive && <span className="um-node__off">비활성</span>}
        {nodeValue(row?.usedBytes ?? 0, row ? (row.unlimited ? -1 : row.limitBytes) : null)}
      </div>
    );
  }

  function renderDivNode(d: OrgDivision) {
    const open = openDivs[d.id] ?? true;
    const isSel = sel.kind === 'div' && sel.id === d.id;
    const visible = teamsOfDiv(d.id).filter((t) => matchQ(t.name) || matchQ(d.name));
    return (
      <div key={d.id}>
        <div role="treeitem" tabIndex={0} aria-selected={isSel} aria-expanded={open}
          className={`um-node${isSel ? ' is-sel' : ''}${d.isActive ? '' : ' is-inactive'}`}
          style={hueStyle(hueOf(d.id))}
          onClick={() => setSel({ kind: 'div', id: d.id })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'div', id: d.id }); } }}>
          <button type="button" className={`um-tw${open ? ' is-open' : ''}`} aria-label={open ? '접기' : '펼치기'}
            onClick={(e) => { e.stopPropagation(); setOpenDivs((p) => ({ ...p, [d.id]: !open })); }}>
            <ChevronRight size={12} />
          </button>
          <span className="um-mk um-mk--dept"><i /></span>
          <span className="um-node__nm">{d.name}</span>
          {!d.isActive && <span className="um-node__off">비활성</span>}
          {nodeValue(divUsed(d.id), divLimit(d.id))}
        </div>
        {open && visible.length > 0 && <div className="um-kids">{visible.map(renderTeamNode)}</div>}
      </div>
    );
  }

  const visibleDivs = sortedDivs.filter((d) => matchQ(d.name) || teamsOfDiv(d.id).some((t) => matchQ(t.name)));
  const visibleRootTeams = rootTeams.filter((t) => matchQ(t.name));

  function renderCrumb() {
    if (sel.kind === 'all') return <b>전체 사용자</b>;
    if (sel.kind === 'none') return <b>회사 직속</b>;
    if (sel.kind === 'div') return <><span>{company || '회사'}</span> <span className="sep">/</span> <b>{scope.name}</b></>;
    return (
      <>
        {scope.sub === '부서 없음'
          ? <span className="sep">부서 없음 / </span>
          : <>{scope.sub} <span className="sep">/</span> </>}
        <b>{scope.name}</b>
      </>
    );
  }

  /* ─────────────────────────── 렌더 ─────────────────────────── */
  return (
    <div className="um-ws wsq-ws">

      {/* ══ 좌: 조직 트리(용량) ══ */}
      <aside className="um-panel um-panel--org">
        <div className="um-ph">
          <HardDrive size={15} />
          <h3>조직 · 용량</h3>
          <span className="um-ph__n">{divs.length}부서 · {teams.length}팀</span>
          <span className="um-ph__sp" />
        </div>

        <div className="um-org__tools">
          <SearchInput
            value={orgQ}
            placeholder="부서 · 팀 검색"
            aria-label="부서 팀 검색"
            onChange={(e) => setOrgQ(e.target.value)}
            onClear={() => setOrgQ('')}
            fullWidth
          />
        </div>

        <nav className="um-tree" aria-label="조직 · 용량" role="tree">
          <div className={`um-node um-node--root${sel.kind === 'all' ? ' is-sel' : ''}`}
            role="treeitem" aria-selected={sel.kind === 'all'} tabIndex={0}
            onClick={() => setSel({ kind: 'all' })}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'all' }); } }}>
            <span className="um-tw is-leaf" />
            <span className="um-mk um-mk--company"><i /></span>
            <span className="um-node__nm">{company || '회사 전체'}</span>
            {nodeValue(
              overview?.totalBytes ?? 0,
              overview ? (overview.company.unlimited ? -1 : overview.company.limitBytes) : null,
            )}
          </div>

          <div className="um-kids">
            {visibleDivs.map(renderDivNode)}
            {visibleRootTeams.map(renderTeamNode)}
            {orgQ.trim() === '' && (
              <div className={`um-node${sel.kind === 'none' ? ' is-sel' : ''}`}
                role="treeitem" aria-selected={sel.kind === 'none'} tabIndex={0}
                onClick={() => setSel({ kind: 'none' })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'none' }); } }}>
                <span className="um-tw is-leaf" />
                <span className="um-mk um-mk--plain"><i /></span>
                <span className="um-node__nm">회사 직속</span>
                {nodeValue(unassigned.reduce((s, r) => s + r.usedBytes, 0), null)}
              </div>
            )}
          </div>

          {orgQ.trim() !== '' && visibleDivs.length === 0 && visibleRootTeams.length === 0 && (
            <p className="um-f__hint" style={{ padding: '10px 9px' }}>검색 결과가 없습니다.</p>
          )}
          {!loading && teams.length === 0 && (
            <p className="um-f__hint um-warn" style={{ padding: '8px 9px 0' }}>
              등록된 팀이 없습니다. 팀 용량은 조직도의 팀을 기준으로 매겨지므로 <strong>사용자 관리</strong> 에서 먼저 만들어 주세요.
            </p>
          )}
        </nav>

        {/* ── 선택 범위 요약 — 한도는 여기서 고친다 ── */}
        <div className="um-orgcard">
          <div className="um-orgcard__t" style={hueStyle(scope.hue)}>
            <b>{scope.name}</b>
            <span className="um-orgcard__kind">{scope.kindLabel}</span>
          </div>

          <div className="wsq-sum">
            <b>{formatBytes(scope.used)}</b>
            {/* 부서는 한도 개념 자체가 없다 — "/ 무제한" 으로 적으면 무제한 한도를 가진 것으로 읽힌다. */}
            <span>
              {scope.kindLabel === '부서'
                ? (scope.limit != null && scope.limit < 0 ? '무제한 팀 포함' : `/ 팀 한도 합계 ${formatBytes(scope.limit ?? 0)}`)
                : scope.limit == null ? '한도 없음'
                  : scope.limit < 0 ? '/ 무제한'
                    : `/ ${formatBytes(scope.limit)}`}
            </span>
            {scope.limit != null && scope.limit > 0 && (
              <em>{Math.round((scope.used / scope.limit) * 100)}%</em>
            )}
          </div>
          {scope.limit != null && scope.limit > 0 && (
            <span className={`wsq-bar wsq-bar--${toneOfPct(scope.used, scope.limit)}`} aria-hidden>
              <i style={{ width: `${Math.min(100, Math.round((scope.used / scope.limit) * 100))}%` }} />
            </span>
          )}

          {sel.kind === 'all' && overview && <KindChips byKind={overview.byKind} />}

          <dl>
            <dt>인원</dt><dd>{scope.people}명</dd>
            <dt>파일</dt><dd>{scope.fileCount.toLocaleString()}개</dd>
            <dt>{scope.kindLabel === '팀' ? '상위 부서' : '구성'}</dt><dd>{scope.sub}</dd>
          </dl>

          <div className="um-orgcard__act">
            {scope.editable ? (
              <Button
                variant="primary"
                size="compact"
                iconLeft={<Edit2 size={13} aria-hidden />}
                onClick={() => {
                  const ed = scope.editable!;
                  setTarget({
                    scopeType: ed.scopeType, scopeId: ed.scopeId, row: ed.row,
                    kindLabel: scope.kindLabel, defaultLimit: ed.defaultLimit,
                  });
                }}
              >
                {scope.kindLabel} 한도 수정
              </Button>
            ) : (
              <span className="um-f__hint">
                {sel.kind === 'div'
                  ? '부서에는 한도가 없습니다 — 한도는 팀 단위로 매겨집니다. 위 값은 하위 팀의 합계입니다.'
                  : '회사 직속 인원에는 팀 한도가 없습니다 — 개인 한도만 적용됩니다.'}
              </span>
            )}
          </div>
        </div>
      </aside>

      {/* ══ 우: 그 범위의 사람 ══ */}
      <section className="um-panel um-panel--user">
        <div className="um-ph">
          <span className="um-crumb">
            {renderCrumb()} <span className="cnt">{scopedPeople.length}명</span>
          </span>
          <span className="um-ph__sp" />
          {defaults && <span className="um-ph__n">개인 기본 {formatBytes(defaults.personal)}</span>}
        </div>

        <div className="um-bar">
          <SearchInput
            value={q}
            placeholder="이름 · 이메일"
            aria-label="사용자 검색"
            onChange={(e) => setQ(e.target.value)}
            onClear={() => setQ('')}
            /* 줄에서 남는 폭을 이 칸이 먹는다 — 옛 `.um-search` 가 하던 몫(테두리는 컴포넌트가 그린다). */
            wrapperClassName="wsq-search"
          />
          {/* ★ 화살표는 고르개가 제 안에 그린다 — 옆에 `.um-selw` 로 겹쳐 두던 것을 걷어냈다. */}
          <span style={{ width: 132 }}>
            <Select value={tone} onChange={setTone} ariaLabel="용량 상태 필터" options={TONE_OPTS} />
          </span>
          <span style={{ width: 152 }}>
            <Select value={sort} onChange={setSort} ariaLabel="정렬 기준" options={SORT_OPTS} />
          </span>
        </div>

        {err && <div className="um-alert" role="alert"><AlertTriangle size={15} /><span>{err}</span></div>}
        {notice && (
          <div className={`um-alert ${notice.tone === 'warn' ? 'um-alert--warn' : 'um-alert--ok'}`} role="status">
            <Check size={15} /><span>{notice.text}</span>
          </div>
        )}

        {pickedIds.length > 0 && (
          <div className="um-bulk">
            <span className="um-bulk__n">{pickedIds.length}명 선택</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>한도</span>
            <span className="wsq-bulk__gbw">
              <NumberInput
                value={bulkGb}
                min={0}
                step={1}
                unit="GB"
                aria-label="일괄 적용할 한도 (GB)"
                onValueChange={(v) => setBulkGb(Math.max(0, v ?? 0))}
                fullWidth
              />
            </span>
            <Button variant="primary" size="compact" disabled={bulkBusy} loading={bulkBusy}
              onClick={() => void applyBulk(gbToBytes(bulkGb))}>
              {bulkBusy ? '적용 중…' : '일괄 적용'}
            </Button>
            <Button variant="secondary" size="compact" disabled={bulkBusy}
              onClick={() => void applyBulk(-1)}>무제한으로</Button>
            <span className="um-bulk__sp" />
            <Button variant="ghost" size="compact" onClick={() => setPicked({})}>선택 해제</Button>
          </div>
        )}

        <div className="um-lh">
          <Checkbox
            aria-label="이 페이지 전체 선택"
            checked={paged.length > 0 && paged.every((r) => picked[r.scopeId])}
            indeterminate={paged.some((r) => picked[r.scopeId]) && !paged.every((r) => picked[r.scopeId])}
            onChange={(e) => {
              const on = e.target.checked;
              setPicked((p) => {
                const next = { ...p };
                paged.forEach((r) => { if (on) next[r.scopeId] = true; else delete next[r.scopeId]; });
                return next;
              });
            }}
          />
          <span><b>{filtered.length}</b>명</span>
          <span className="um-lh__sp" />
          <span className="wsq-scopesum">이 범위 합계 {formatBytes(scopeUsed)}</span>
        </div>

        <div className="um-list">
          {loading ? (
            Array.from({ length: 8 }, (_, i) => (
              <div className="um-row" key={i} aria-hidden="true">
                <span /><span className="um-skel um-skel--av" />
                <span style={{ minWidth: 0 }}>
                  <span className="um-skel" style={{ width: 120, display: 'block' }} />
                  <span className="um-skel" style={{ width: '70%', marginTop: 9, display: 'block' }} />
                </span>
                <span className="um-skel" style={{ width: 140, height: 20, borderRadius: 6 }} />
                <span className="um-skel" style={{ width: 52, height: 20, borderRadius: 999 }} /><span />
              </div>
            ))
          ) : paged.length === 0 ? (
            <div className="um-blank">
              <Users size={30} style={{ color: 'var(--text-subtle)' }} />
              <b>{scopedPeople.length === 0 ? '이 범위에는 인원이 없습니다' : '조건에 맞는 사용자가 없습니다'}</b>
              <p>
                {scopedPeople.length === 0
                  ? '왼쪽 트리에서 다른 조직을 골라 보세요. 소속은 사용자 관리 화면에서 바꿉니다.'
                  : '필터를 넓히거나 검색어를 지워 보세요.'}
              </p>
              {scopedPeople.length > 0 && (
                <Button variant="secondary" size="compact"
                  onClick={() => { setQ(''); setTone(''); }}>필터 초기화</Button>
              )}
            </div>
          ) : paged.map((r) => {
            const t = toneOf(r);
            const off = inactiveLabel(r);
            const tids = memberOf[r.scopeId] ?? [];
            const firstTeam = tids.length > 0 ? teamById.get(tids[0]!) : undefined;
            const hue = firstTeam ? teamHue(firstTeam) : 'var(--text-subtle)';
            return (
              <div key={r.scopeId}
                className={`um-row${picked[r.scopeId] ? ' is-sel' : ''}${off ? ' is-off' : ''}`}
                role="row" tabIndex={0}
                onClick={() => setTarget({ scopeType: 'personal', scopeId: r.scopeId, row: r, kindLabel: '개인', defaultLimit: defaults?.personal ?? null })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setTarget({ scopeType: 'personal', scopeId: r.scopeId, row: r, kindLabel: '개인', defaultLimit: defaults?.personal ?? null }); } }}>

                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={!!picked[r.scopeId]}
                    aria-label={`${r.name} 선택`}
                    onChange={() => setPicked((p) => {
                      const next = { ...p };
                      if (next[r.scopeId]) delete next[r.scopeId]; else next[r.scopeId] = true;
                      return next;
                    })}
                  />
                </span>

                <span className="um-av" style={hueStyle(hue)} aria-hidden="true">
                  {initial(r.name, r.subtitle ?? '')}
                </span>

                <span style={{ minWidth: 0 }}>
                  <span className="um-id">
                    <b>{r.name}</b>
                    {r.isDefault && !r.unlimited && <span className="um-tag">기본</span>}
                    {off && <span className="um-tag">{off}</span>}
                  </span>
                  <span className="um-meta">
                    <span className="org" style={hueStyle(hue)}>
                      <i />{firstTeam ? firstTeam.name : '회사 직속'}{tids.length > 1 ? ` 외 ${tids.length - 1}` : ''}
                    </span>
                    {r.position && <span>{r.position}</span>}
                    {r.subtitle && <span>{r.subtitle}</span>}
                    <span className="mono">파일 {r.fileCount.toLocaleString()}개</span>
                  </span>
                </span>

                <span className={`wsq-use wsq-use--${t}`}>
                  <span className="wsq-use__n">
                    {formatBytes(r.usedBytes)}
                    <span className="sep"> / </span>
                    <span className="lim">{r.unlimited ? '무제한' : formatBytes(r.limitBytes)}</span>
                    {!r.unlimited && <span className="wsq-use__pct">{r.pct}%</span>}
                  </span>
                  {!r.unlimited && (
                    <span className={`wsq-bar wsq-bar--${t}`} role="progressbar"
                      aria-label={`${r.name} 사용률`} aria-valuemin={0} aria-valuemax={100}
                      aria-valuenow={Math.min(100, r.pct)}>
                      <i style={{ width: `${Math.min(100, r.pct)}%` }} />
                    </span>
                  )}
                </span>

                <span className={`um-st um-st--${t} wsq-st`}>{TONE_LABEL[t]}</span>

                <Button
                  variant="ghost"
                  size="compact"
                  iconOnly
                  aria-label={`${r.name} 한도 수정`}
                  iconLeft={<MoreVertical size={15} aria-hidden />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTarget({ scopeType: 'personal', scopeId: r.scopeId, row: r, kindLabel: '개인', defaultLimit: defaults?.personal ?? null });
                  }}
                />
              </div>
            );
          })}
        </div>

        {!loading && filtered.length > 0 && (
          <div className="um-foot">
            <span>
              전체 <b>{filtered.length}</b>명 중 <b>{(curPage - 1) * PAGE_SIZE + 1}–{Math.min(filtered.length, curPage * PAGE_SIZE)}</b>
            </span>
            {/* ★ 쪽이 많아도 다 그리지 않는다 — 정본 Pagination 이 양 끝과 지금 쪽 둘레만 남긴다. */}
            <Pagination
              mode="offset"
              page={curPage}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPage}
            />
          </div>
        )}
      </section>

      {target && (
        <QuotaDrawer
          key={`${target.scopeType}:${target.scopeId ?? 'company'}`}
          target={target}
          onClose={() => setTarget(null)}
          onSaved={(saved) => {
            if (target.scopeType === 'personal' && target.scopeId) applySaved('personal', target.scopeId, saved);
            else if (target.scopeType === 'team' && target.scopeId) applySaved('team', target.scopeId, saved);
            setTarget(null);
            setNotice({ text: `${target.row.name} 의 용량 한도를 저장했습니다.`, tone: 'ok' });
            if (target.scopeType !== 'personal') void reload();
            else void getWorkspaceOverview().then(setOverview).catch(() => { /* 요약 갱신 실패는 저장을 되돌리지 않는다 */ });
          }}
        />
      )}
    </div>
  );
}

/**
 * 종류별 칩 — 회사 범위에서만 뜻이 있다(서버 overview 가 회사 단위 집계라서).
 * 서버는 FILE_KINDS 9종을 **전부** 내려보낸다(없는 종류는 0). 그대로 그리면 빈 칩이
 * 도배되므로 실제로 파일이 있는 것만, 용량 큰 것부터.
 */
const KIND_CHIPS_MAX = 6;
function KindChips({ byKind }: { byKind: WorkspaceOverview['byKind'] }) {
  const kinds = byKind.filter((k) => k.count > 0).slice().sort((a, b) => b.bytes - a.bytes);
  if (kinds.length === 0) return null;
  // 카드가 높아지면 그만큼 트리가 잘린다 — 큰 것 몇 개만 보이고 나머지는 하나로 접는다.
  const head = kinds.slice(0, KIND_CHIPS_MAX);
  const rest = kinds.slice(KIND_CHIPS_MAX);
  const restBytes = rest.reduce((s, k) => s + k.bytes, 0);
  return (
    <div className="wsq-kinds" aria-label="파일 종류별 용량">
      {head.map((k) => (
        <span key={k.kind} className="wsq-kind" title={`${k.count.toLocaleString()}개`}>
          <b>{KIND_LABEL[k.kind] ?? k.kind}</b>
          <span className="mono">{formatBytes(k.bytes)}</span>
        </span>
      ))}
      {rest.length > 0 && (
        <span className="wsq-kind" title={rest.map((k) => `${KIND_LABEL[k.kind] ?? k.kind} ${formatBytes(k.bytes)}`).join(' · ')}>
          <b>그 밖 {rest.length}종</b>
          <span className="mono">{formatBytes(restBytes)}</span>
        </span>
      )}
    </div>
  );
}

/* ─────────────────────── 한도 수정 드로어 ─────────────────────── */

function QuotaDrawer({ target, onClose, onSaved }: {
  target: Target;
  onClose: () => void;
  onSaved: (saved: { limitBytes: number; pct: number; warn: boolean; isDefault: boolean }) => void;
}) {
  const { row, kindLabel, defaultLimit } = target;
  const [unlimited, setUnlimited] = useState(row.unlimited);
  const [gb, setGb] = useState(() => (row.unlimited
    ? bytesToGb(defaultLimit != null && defaultLimit > 0 ? defaultLimit : row.usedBytes)
    : bytesToGb(row.limitBytes)));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const tone = toneOf(row);
  const belowUsage = !unlimited && gbToBytes(gb) < row.usedBytes;

  async function save() {
    setSaving(true); setErr(null);
    try {
      const res = await setWorkspaceQuota({
        scopeType: target.scopeType,
        scopeId: target.scopeType === 'company' ? undefined : target.scopeId,
        limitBytes: unlimited ? -1 : gbToBytes(gb),
      });
      onSaved(res);
    } catch (e) {
      setErr(errMsg(e, '저장에 실패했습니다.'));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="um-scrim" role="presentation" onClick={onClose} />
      <aside className="um-dr" role="dialog" aria-modal="true" aria-label="용량 한도 수정">
        <div className="um-dr__h">
          <span className="um-av" aria-hidden="true">{initial(row.name, row.subtitle ?? '')}</span>
          <span className="um-dr__t">
            <b>{row.name}</b>
            <span>{kindLabel}{row.subtitle ? ` · ${row.subtitle}` : ''}</span>
          </span>
          <span className={`um-st um-st--${tone}`}>{TONE_LABEL[tone]}</span>
          <Button
            variant="ghost"
            size="compact"
            iconOnly
            aria-label="닫기"
            onClick={onClose}
            iconLeft={<X size={15} aria-hidden />}
          />
        </div>

        <div className="um-dr__b">
          {err && <div className="um-alert" role="alert" style={{ margin: '0 0 14px' }}><AlertTriangle size={15} /><span>{err}</span></div>}

          <div className="um-secl">현재 사용량</div>
          <div className="wsq-use wsq-use--lg">
            <span className="wsq-use__n">
              {formatBytes(row.usedBytes)}
              <span className="sep"> / </span>
              <span className="lim">{row.unlimited ? '무제한' : formatBytes(row.limitBytes)}</span>
            </span>
            {!row.unlimited && (
              <span className={`wsq-bar wsq-bar--${tone}`} aria-hidden>
                <i style={{ width: `${Math.min(100, row.pct)}%` }} />
              </span>
            )}
            <span className="um-f__hint">
              파일 {row.fileCount.toLocaleString()}개{row.unlimited ? '' : ` · 한도의 ${row.pct}%`}
            </span>
          </div>

          <div className="um-secl">한도</div>
          <div className="um-fields">
            <div className="um-f w2">
              <Checkbox checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} label="무제한" />
              <span className="um-f__hint">한도 없이 업로드를 허용합니다. 끄면 아래에서 GB 한도를 지정합니다.</span>
            </div>

            {!unlimited && (
              <div className="um-f w2">
                <label htmlFor="wsq-gb">한도 (GB)</label>
                <NumberInput
                  id="wsq-gb"
                  value={gb}
                  min={0}
                  step={1}
                  steppers
                  unit="GB"
                  onValueChange={(v) => setGb(Math.max(0, v ?? 0))}
                  fullWidth
                />
                <span className="um-f__hint">
                  {defaultLimit != null && defaultLimit >= 0 && `기본값 ${formatBytes(defaultLimit)}. `}
                  한도를 넘으면 새 업로드가 차단됩니다(기존 파일은 그대로 유지).
                </span>
              </div>
            )}
          </div>

          {belowUsage && (
            <div className="um-alert um-alert--warn" role="alert">
              <AlertTriangle size={15} />
              <span>현재 사용량({formatBytes(row.usedBytes)})보다 작은 한도입니다 — 저장 즉시 이 대상의 업로드가 전부 막힙니다.</span>
            </div>
          )}
        </div>

        <div className="um-dr__f">
          <Button variant="primary" disabled={saving} loading={saving} onClick={() => void save()}>
            {saving ? '저장 중…' : '저장'}
          </Button>
          <Button variant="secondary" onClick={onClose}>닫기</Button>
          <span className="sp" />
        </div>
      </aside>
    </>
  );
}
